import { DragEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, Grid2 as Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { color } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { useListSubflowTemplates } from '@/queries/useSubflowTemplates';
import { useFetchMenuItems } from '@/hooks/useFetchMenuItems';
import { useUserStore } from '@/state/user.state';
import { WorkspaceRole } from '@/types/auth.types';
import { EditAssetDialog } from '@/components/SubflowTemplates/EditAssetDialog';
import { useDeleteAssetConfirmation } from '@/components/SubflowTemplates/useDeleteAssetConfirmation';
import { LeftPanelMenuItem } from './LeftPanelMenuItem';
import type { SubflowTemplateResponseDto } from '@/types/api/subflowTemplate';
import type { ModelItem, MenuCategory } from '@/state/nodes/nodes.types';

interface SavedSectionProps {
  node: MenuCategory | undefined;
  titleKey: string;
  emptyKey: string;
  isEditMode: boolean;
}

// Display reads from the menu store. Create/edit/delete mutations refetch the menu on
// success to keep the store in sync (see SaveNodeModal, EditAssetDialog, useDeleteAssetConfirmation).
export function SavedSection({ node, titleKey, emptyKey, isEditMode }: SavedSectionProps) {
  const { t } = useTranslation();
  const items = (node?.children ?? []) as ModelItem[];
  const currentUserId = useUserStore((state) => state.user?.id);
  const isWorkspaceAdmin = useUserStore((state) => state.user?.activeWorkspace?.role === WorkspaceRole.Admin);

  const { data: templates, isSuccess } = useListSubflowTemplates();
  const templateById = useMemo(() => {
    const map = new Map<string, SubflowTemplateResponseDto>();
    for (const item of templates ?? []) {
      map.set(item.id, item);
    }
    return map;
  }, [templates]);

  // On first load the menu is fetched before the server-side migration runs, so its
  // saved-models section comes back empty. Refetch once we know there are rows.
  const { fetchMenuItems } = useFetchMenuItems();
  const hasRefetchedMenuRef = useRef(false);
  useEffect(() => {
    if (isSuccess && templates && templates.length > 0 && !hasRefetchedMenuRef.current) {
      hasRefetchedMenuRef.current = true;
      void fetchMenuItems();
    }
  }, [isSuccess, templates, fetchMenuItems]);

  const onDragStart = useCallback(
    (e: DragEvent<HTMLDivElement>, item: ModelItem) => {
      const asset = templateById.get(item.id);
      if (!asset) {
        return;
      }
      const menuItem = {
        id: asset.sourceNodeDefinitionId ?? asset.id,
        type: asset.type,
        name: asset.name,
        displayName: asset.name,
        savedTemplateData: asset.data,
      };
      e.dataTransfer.setData('menuItem', JSON.stringify(menuItem));
      e.dataTransfer.effectAllowed = 'move';
      if (e.target instanceof HTMLElement) {
        e.target.classList.add('dragging');
      }
      document.body.style.cursor = 'grabbing';
    },
    [templateById],
  );

  const onDragEnd = useCallback((e: DragEvent<HTMLDivElement>) => {
    if (e.target instanceof HTMLElement) {
      e.target.classList.remove('dragging');
    }
    document.body.style.removeProperty('cursor');
  }, []);

  const [editingAsset, setEditingAsset] = useState<SubflowTemplateResponseDto | null>(null);
  const confirmDelete = useDeleteAssetConfirmation();

  const handleEdit = useCallback(
    (item: ModelItem) => {
      const asset = templateById.get(item.id);
      if (asset) {
        setEditingAsset(asset);
      }
    },
    [templateById],
  );

  const handleDelete = useCallback(
    (item: ModelItem) => {
      const asset = templateById.get(item.id);
      if (asset) {
        confirmDelete(asset);
      }
    },
    [templateById, confirmDelete],
  );

  return (
    <>
      <Box>
        <Typography variant="label-sm-rg" component="div" sx={{ mb: 2, color: color.White64_T }}>
          {t(titleKey)}
        </Typography>
        {items.length > 0 ? (
          <Grid container spacing={1}>
            {items.map((item) => {
              const template = templateById.get(item.id);
              const isOwner = !!currentUserId && template?.createdBy === currentUserId;
              const canModify = isOwner || isWorkspaceAdmin;
              return (
                <Grid key={item.id} size={6}>
                  <LeftPanelMenuItem
                    item={item}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                    isEditMode={isEditMode}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    canModify={canModify}
                    template={template}
                  />
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Typography variant="body-sm-rg" component="p" sx={{ color: color.White40_T, py: 2, m: 0 }}>
            {t(emptyKey)}
          </Typography>
        )}
      </Box>

      {editingAsset && <EditAssetDialog asset={editingAsset} onClose={() => setEditingAsset(null)} />}
    </>
  );
}

export function ComingSoonSection({ titleKey }: { titleKey: string }) {
  const { t } = useTranslation();
  return (
    <Box>
      <Typography variant="label-sm-rg" component="div" sx={{ mb: 2, color: color.White64_T }}>
        {t(titleKey)}
      </Typography>
      <Typography variant="body-sm-rg" component="p" sx={{ color: color.White40_T, py: 2, m: 0, textAlign: 'center' }}>
        {t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.WORKSPACE_ASSETS.COMING_SOON)}
      </Typography>
    </Box>
  );
}
